import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Select, Switch, Tooltip, Typography } from 'antd';
import { ReactComponent as InfoSvg } from '@assets/icons/info-icon.svg';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { ErrorPage404, ErrorPage500 } from '@pages/ErrorPage';
import './AsanaInstall.less';
import { isAxiosError } from 'axios';

const { Title } = Typography;

interface AsanaOAuthInstallProps {
  loader: ReactElement;
}

export default function AsanaOAuthInstall({ loader }: AsanaOAuthInstallProps) {
  const [searchParams] = useSearchParams();
  const asanaWorkspaceId = searchParams.get('domainId');
  const [step, setStep] = useState<'org' | 'options'>('org');
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const [createReviewerSubtasks, setCreateReviewerSubtasks] =
    useState<boolean>(false);
  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const {
        data: { list }
      } =
        await apiClient.organization.organizationControllerListUserOrganizations();
      return list;
    }
  });
  const organizations = useMemo(
    () => organizationsQuery.data || [],
    [organizationsQuery.data]
  );

  const mutation = useMutation({
    mutationFn: async ({
      organizationId,
      createReviewerSubtasks
    }: {
      organizationId: string;
      createReviewerSubtasks: boolean;
    }) => {
      const {
        data: { url, handle }
      } = await apiClient.asana.asanaControllerInstall({
        asanaWorkspaceId: nonNull(asanaWorkspaceId),
        organizationId,
        createReviewerSubtasks
      });

      if (!url || !handle) {
        return;
      }

      sessionStorage.setItem('asana-oauth-handle', handle);
      window.location.href = url;
    }
  });
  const { mutate } = mutation;

  const defaultOrgId =
    organizations.length === 1 ? organizations[0].id : undefined;

  const isLoading =
    organizationsQuery.isLoading || mutation.isPending || mutation.isSuccess;

  const isError = organizationsQuery.isError || mutation.isError;

  useEffect(() => {
    if (defaultOrgId) {
      setSelectedOrganizationId(defaultOrgId);
      setStep('options');
    }
  }, [defaultOrgId]);

  if (!asanaWorkspaceId) {
    return <ErrorPage404 onGoBack={() => window.close()} />;
  }

  if (isError) {
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_permission_denied'
    ) {
      return (
        <div className="asana-install">
          <div className="asana-install__content">
            <div className="asana-install__lock-icon">
              <LockSvg />
            </div>
            <Title level={2} className="asana-install__title">
              You don&apos;t have permissions to install integrations
            </Title>
            <Title level={5} className="asana-install__description">
              Contact your organization admin to install the Asana integration
            </Title>
          </div>
        </div>
      );
    }
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_already_has_asana_integration'
    ) {
      return (
        <div className="asana-install">
          <div className="asana-install__content">
            <Title level={2} className="asana-install__title">
              This organization already has an Asana integration installed
            </Title>
            <Title level={5} className="asana-install__description">
              Contact{' '}
              <a href="mailto:support@streamwork.com">support@streamwork.com</a>{' '}
              if you believe this is a mistake
            </Title>
          </div>
        </div>
      );
    }
    return <ErrorPage500 onGoBack={() => window.close()} />;
  }

  if (isLoading) {
    return loader;
  }

  if (step === 'org') {
    return (
      <div className="asana-install">
        <div className="asana-install__content">
          <Title level={2} className="asana-install__title">
            Select Organization
          </Title>
          <Title level={5} className="asana-install__description">
            Choose an organization to connect with Asana workspace
          </Title>
          <Select
            className="asana-install__select"
            size="large"
            loading={organizationsQuery.isLoading}
            placeholder="Select an organization..."
            onChange={setSelectedOrganizationId}
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
          <Button
            className="asana-install__button"
            type="primary"
            onClick={() => setStep('options')}
            disabled={!selectedOrganizationId}
          >
            Next
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="asana-install">
      <div className="asana-install__content">
        <Title level={2} className="asana-install__title">
          Integration Options
        </Title>
        <Title level={5} className="asana-install__description">
          Configure how StreamWork integrates with your Asana workspace
        </Title>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12
          }}
        >
          <Switch
            checked={createReviewerSubtasks}
            onChange={setCreateReviewerSubtasks}
          />
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Create a subtask for each reviewer in Asana
            <Tooltip
              overlayClassName="asana-install__tooltip"
              title="When an approval workflow is attached to an Asana task, StreamWork will automatically create a subtask for each reviewer, making it easy to track individual review progress directly in Asana."
            >
              <InfoSvg />
            </Tooltip>
          </span>
        </div>
        <Button
          className="asana-install__button"
          type="primary"
          loading={mutation.isPending}
          onClick={() =>
            mutate({
              organizationId: selectedOrganizationId,
              createReviewerSubtasks
            })
          }
        >
          Connect
        </Button>
      </div>
    </div>
  );
}
